import { api } from '../index';

// Short-lived token issued by /account/login when 2FA is pending. It only unlocks the
// three 2FA endpoints below, so it lives in sessionStorage (tab-scoped) rather than localStorage.
const PRE_AUTH_KEY = 'preAuthToken';

export const storePreAuthToken = (token) => sessionStorage.setItem(PRE_AUTH_KEY, token);
export const getPreAuthToken = () => sessionStorage.getItem(PRE_AUTH_KEY);
export const clearPreAuthToken = () => sessionStorage.removeItem(PRE_AUTH_KEY);

const preAuthConfig = () => ({ headers: { Authorization: `Bearer ${getPreAuthToken()}` } });

// A 401/403 from a 2FA endpoint means the server no longer accepts the pre-auth token (expired or
// revoked). Drop it so the route guard sends the user back to login instead of retrying a dead challenge.
const isChallengeExpired = (error) => error?.status === 401 || error?.status === 403;

const withPreAuth = async (call) => {
  try {
    return await call(preAuthConfig());
  } catch (error) {
    if (isChallengeExpired(error)) {
      clearPreAuthToken();
    }
    throw error;
  }
};

export const fetchQrCode = () => withPreAuth((config) => api.get('/account/qr-code', config));

export const fetchSetupKey = () => withPreAuth((config) => api.get('/account/secret', config));

export const verifyOtp = async (otp) => {
  const resp = await withPreAuth((config) => api.post('/account/verify-otp', { otp }, config));
  if (resp?.data?.code === 0 && resp.data.data?.token) {
    clearPreAuthToken();
  }
  return resp;
};

// PT-07: the 2FA endpoints are rate limited per IP. Prefer the server's message (a 429 says how long to
// wait); otherwise say so generically instead of "Network Error".
export const TOO_MANY_ATTEMPTS = 'Too many attempts. Please wait a few minutes and try again.';
export const CHALLENGE_EXPIRED = 'Your verification session has expired. Please log in again.';
export const otpErrorMessage = (error) => {
  if (isChallengeExpired(error)) return CHALLENGE_EXPIRED;
  return error?.data?.message || (error?.status === 429 ? TOO_MANY_ATTEMPTS : 'Network Error');
};
